import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withTheme } from 'styled-components';
import isNil from 'lodash/isNil';
import { Icon, type Theme } from 'venn-ui-kit';
import { useRecoilValue } from 'recoil';
import { type PortfolioBreakdownHierarchyNode } from 'venn-components';
import { useSetBlockSize } from '../../logic/useSetBlockSize';
import { Numbers } from 'venn-utils';
import {
  blockFonts,
  blockLimitedRequestSubjects,
  isReportState,
  recoilBlockContentSize,
  recoilBlockMaxSize,
  studioBlockCustomWidth,
  vdparFundIdsState,
} from 'venn-state';
import { usePageVdparSymbol } from '../../../blocks/components/core/block-subject-footnotes-and-superscripts';
import { cn } from '@/lib/utils';

import type { GeneralChartProps } from './types';
import { LegendPosition } from '../grid/types';
import { usePortfolioMultiFiltering } from '../../../blocks/utils/hooks/use-portfolio-multi-filtering';
import { createVennPieChartConfig } from '../../../charts/analysis-charts/chart-config-logic';
import DownloadableContentBlock from '../../../content-block/DownloadableContentBlock';
import { WATERMARK_POSITION_TOP } from '../../customAnalysisContants';
import { usePieChartDetails } from '../../logic/usePieChartDetails';
import useExportUpdate from '../../logic/useExportUpdate';
import { GraphicSubjectLabel } from './GraphicSubjectLabel';
import { useBlockId } from '../../contexts/BlockIdContext';
import { StudioHighchart } from '../../../highchart/Highchart';
import type HighchartsReact from 'highcharts-react-official';
import Measure, { type ContentRect } from 'react-measure';

export interface PieChartDisplayProps extends GeneralChartProps {
  theme: Theme;
  inPrintMode?: boolean;
  height?: number;
  hideSubject?: boolean;
}

const MIN_DONUT_SIZE = 150;
const MAX_DONUT_SIZE = 400;
const LEGEND_GAP = 15;
const LEGEND_WIDTH = 250;
const STUDIO_BESIDE_MIN_WIDTH = MIN_DONUT_SIZE + LEGEND_GAP + LEGEND_WIDTH;

const LegendNode = ({
  nodes,
  colorMap,
  level = 0,
  depth = 1,
  vdparFundIds,
  vdparSymbol,
}: {
  nodes: PortfolioBreakdownHierarchyNode[];
  colorMap: Map<string, string>;
  level?: number;
  depth?: number;
  vdparFundIds: Set<string>;
  vdparSymbol: string | undefined;
}) => {
  return (
    <ul
      className={cn([
        'list-none pl-0 flex flex-col',
        level ? 'ml-[1em]' : depth === 1 ? 'gap-y-[.25em]' : 'gap-y-[.5em]',
      ])}
    >
      {nodes?.map((node) => (
        <li key={node.id} className={cn('my-[.25em] shrink-0')}>
          <div className={cn(['flex items-start gap-[.5em]', level === 0 && depth ? 'font-bold' : ''])}>
            <div className={cn('flex items-center gap-[.5em]')}>
              <Icon style={{ color: colorMap.get(node.id) }} type="square" />
              <span>{Numbers.safeFormatPercentage(node.weight, 1)}</span>
            </div>
            <span>
              {node.label}
              {node.fundId && vdparFundIds.has(node.fundId) && <sup>{vdparSymbol}</sup>}
            </span>
          </div>

          {node.children?.length ? (
            <LegendNode
              nodes={node.children}
              colorMap={colorMap}
              level={level + 1}
              vdparFundIds={vdparFundIds}
              vdparSymbol={vdparSymbol}
            />
          ) : undefined}
        </li>
      ))}
    </ul>
  );
};

const PieChart = ({
  theme,
  metrics,
  inPrintMode,
  height,
  selectedBlock,
  customBlockType: blockType,
  downloadableContentRef,
  responseParser,
  analyses,
  requests,
  selectedRefId,
  hideSubject = false,
  ...restProps
}: PieChartDisplayProps) => {
  if (isNil(blockType)) {
    throw new Error('Expected customBlockType to be not nil in in PieChart.');
  }

  const blockId = useBlockId();
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(blockId));
  const isReport = useRecoilValue(isReportState);
  const subject = useRecoilValue(blockLimitedRequestSubjects(selectedRefId))[0];
  const onResize = useSetBlockSize(recoilBlockContentSize.transformedState(blockId));
  const { hasMultiLevelPortfolioFiltering, filter, legendPosition } = usePortfolioMultiFiltering(blockId);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);
  const [legendSize, setLegendSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();

  const parsedData = useMemo(
    () => responseParser.parser(selectedBlock, analyses),
    [analyses, responseParser, selectedBlock],
  );

  const excelDataFn = useMemo(() => {
    const excelParser = responseParser.excelParser;
    return excelParser ? () => excelParser(requests, blockType, metrics, parsedData) : undefined;
  }, [responseParser, requests, blockType, metrics, parsedData]);

  useExportUpdate({
    ...restProps,
    selectedRefId,
    excelDataFn,
  });

  const metric = metrics ? metrics[0] : undefined;
  const yAxisUnitFormat = metric?.dataType === 'NUMERIC' ? 'ratio' : 'percent';
  const defaultHeader =
    selectedBlock?.relativeToBenchmark && metric?.relativeLabel
      ? metric.relativeLabel
      : !metric
        ? 'Builder Chart'
        : metric.label;

  const { colorMap, hierarchyTree, seriesEntries } = usePieChartDetails({
    theme,
    blockType,
    metric,
    levels: hasMultiLevelPortfolioFiltering ? filter?.levels : undefined,
    mergeSameLabelItems: filter?.mergeRows,
    data: parsedData,
    hasMultiLevelPortfolioFiltering: !!hasMultiLevelPortfolioFiltering,
  });

  const isPortfolioComposition = blockType === 'PORTFOLIO_BREAKDOWN';
  const useLegendPositioning = isPortfolioComposition && !!hasMultiLevelPortfolioFiltering;
  const blockCustomWidth = useRecoilValue(studioBlockCustomWidth(blockId));
  const blockMaxSize = useRecoilValue(recoilBlockMaxSize.transformedState(blockId));
  const containerWidth = containerSize?.width ?? 0;
  const containerHeight = containerSize?.height ?? 0;
  const studioAutoPosition =
    blockCustomWidth >= 1 && (containerWidth === 0 || containerWidth >= STUDIO_BESIDE_MIN_WIDTH)
      ? LegendPosition.RIGHT
      : LegendPosition.BOTTOM;
  const [autoDetectedLegendPosition, setAutoDetectedLegendPosition] = useState<LegendPosition>(LegendPosition.RIGHT);
  useEffect(() => {
    if (!blockMaxSize) return;
    setAutoDetectedLegendPosition(
      blockMaxSize.width > blockMaxSize.height ? LegendPosition.RIGHT : LegendPosition.BOTTOM,
    );
  }, [blockMaxSize]);

  const effectiveLegendPosition = useLegendPositioning
    ? isReport
      ? (legendPosition ?? autoDetectedLegendPosition)
      : studioAutoPosition
    : LegendPosition.BOTTOM;
  const customLegend = hasMultiLevelPortfolioFiltering || vdparFundIds.size > 0 || !isReport;
  const isLegendBeside = customLegend && effectiveLegendPosition !== LegendPosition.BOTTOM;

  const options = useMemo(
    () =>
      createVennPieChartConfig(
        seriesEntries,
        theme,
        yAxisUnitFormat,
        height,
        legendFont.fontSizePt,
        inPrintMode,
        customLegend,
        false,
        vdparFundIds,
        vdparSymbol,
      ),
    [
      seriesEntries,
      theme,
      yAxisUnitFormat,
      height,
      legendFont.fontSizePt,
      inPrintMode,
      customLegend,
      vdparFundIds,
      vdparSymbol,
    ],
  );

  const chartLegendClasses = cn(
    hasMultiLevelPortfolioFiltering ? 'px-[18px] py-[12px]' : 'flex p-[15px]',
    !hasMultiLevelPortfolioFiltering && 'flex-wrap justify-start gap-x-[36px] gap-y-[6px]',
    isLegendBeside && 'w-[250px] shrink-0',
    useLegendPositioning && !isLegendBeside && 'self-center',
  );
  const chartLegendStyle = { fontSize: `${legendFont.fontSizePt}pt` };
  const chartLegendContent = !customLegend
    ? null
    : hasMultiLevelPortfolioFiltering
      ? hierarchyTree &&
        colorMap && (
          <LegendNode
            nodes={hierarchyTree}
            colorMap={colorMap}
            depth={seriesEntries?.length}
            vdparFundIds={vdparFundIds}
            vdparSymbol={vdparSymbol}
          />
        )
      : seriesEntries?.[0]?.data?.map((node) => (
          <div key={node.name} className={cn('flex gap-x-[3px]')}>
            <Icon style={{ color: node.color as string }} type="circle" />
            <span>
              {node.name}
              {node.fundId && vdparFundIds.has(node.fundId) && <sup>{vdparSymbol}</sup>}
              {`: ${Numbers.safeFormatPercentage(node.y, 1)}`}
            </span>
          </div>
        ));

  const onLegendResize = useCallback((rect: ContentRect) => {
    const w = rect.bounds?.width ?? 0;
    const h = rect.bounds?.height ?? 0;
    setLegendSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
  }, []);

  const chartLegend = customLegend ? (
    useLegendPositioning ? (
      <Measure bounds onResize={onLegendResize}>
        {({ measureRef: legendMeasureRef }) => (
          <div ref={legendMeasureRef} className={chartLegendClasses} style={chartLegendStyle}>
            {chartLegendContent}
          </div>
        )}
      </Measure>
    ) : (
      <div className={chartLegendClasses} style={chartLegendStyle}>
        {chartLegendContent}
      </div>
    )
  ) : undefined;

  const onResizeWithReflow = useCallback(
    (contentRect: ContentRect) => {
      chartRef?.current?.chart.reflow();
      const bounds = contentRect.bounds;
      if (!bounds) return;
      setContainerSize((prev) =>
        prev && prev.width === bounds.width && prev.height === bounds.height
          ? prev
          : { width: bounds.width, height: bounds.height },
      );
    },
    [chartRef],
  );

  const belowDonutSize = Math.max(
    MIN_DONUT_SIZE,
    Math.min(MAX_DONUT_SIZE, containerWidth, containerHeight - legendSize.height),
  );

  useEffect(() => {
    if (!containerSize) return;
    if (!useLegendPositioning) {
      onResize({ bounds: containerSize });
      return;
    }
    const naturalWidth = isLegendBeside ? MIN_DONUT_SIZE + LEGEND_GAP + LEGEND_WIDTH : containerSize.width;
    const naturalHeight = isLegendBeside
      ? Math.max(MIN_DONUT_SIZE, legendSize.height)
      : belowDonutSize + legendSize.height;
    onResize({ bounds: { width: naturalWidth, height: naturalHeight } });
  }, [useLegendPositioning, isLegendBeside, legendSize, belowDonutSize, containerSize, onResize]);

  return (
    <DownloadableContentBlock
      header=""
      noBorder
      downloadable={{
        png: true,
        options: {
          fileName: selectedBlock?.header ? selectedBlock?.header : defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark: !!selectedBlock?.relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: !seriesEntries?.length,
        target: downloadableContentRef,
      }}
      floatingOptions
    >
      <Measure onResize={onResizeWithReflow} bounds>
        {({ measureRef, contentRect }) => (
          <div ref={measureRef} className="w-full h-full">
            <div
              className={cn(
                'w-full h-full',
                useLegendPositioning && 'overflow-hidden',
                isLegendBeside && 'flex flex-row items-start justify-center gap-[15px]',
                !isLegendBeside && useLegendPositioning && 'flex flex-col items-center justify-start',
                !isLegendBeside && !useLegendPositioning && 'flex flex-col justify-center',
              )}
            >
              {/* TODO: Confirm if its necessary to show subject here. In our newer block architecture, all blocks should be rendered in the header  */}
              {!hideSubject && subject && (isReport || inPrintMode) && <GraphicSubjectLabel subject={subject} />}
              {effectiveLegendPosition === LegendPosition.LEFT ? chartLegend : null}
              {useLegendPositioning ? (
                <Measure bounds>
                  {({ contentRect: chartRect, measureRef: chartMeasureRef }) => (
                    <div
                      ref={chartMeasureRef}
                      style={!isLegendBeside ? { width: belowDonutSize, height: belowDonutSize } : undefined}
                      className={cn(
                        isLegendBeside && 'flex-1 aspect-square min-w-[150px] min-h-[150px] max-w-[400px] max-h-full',
                        !isLegendBeside && 'shrink-0',
                      )}
                    >
                      <StudioHighchart
                        bounds={chartRect.bounds}
                        options={options}
                        blockId={blockId}
                        chartRef={chartRef}
                        className="w-full h-full"
                      />
                    </div>
                  )}
                </Measure>
              ) : (
                <StudioHighchart
                  bounds={contentRect.bounds}
                  options={options}
                  blockId={blockId}
                  chartRef={chartRef}
                  className={cn(['flex-grow aspect-square', !isReport && 'min-h-[75px] max-h-[400px]'])}
                />
              )}
              {effectiveLegendPosition !== LegendPosition.LEFT ? chartLegend : null}
            </div>
          </div>
        )}
      </Measure>
    </DownloadableContentBlock>
  );
};

export default withTheme(PieChart);
